import React from "react";
import { Head, Link, usePage } from "@inertiajs/react";
import styled, { keyframes } from "styled-components";
import { GlobalStyle } from "../../constants/global-style";
import { getCsrfToken } from "../../util";
import { brand_orange_deep, brand_success, dark } from "../../constants/styles";
import AuthLayout from "../../auth/AuthLayout";
import SubmitButton from "../../auth/SubmitButton";

// Email-confirmation landing. Reached from the tautan in the confirmation
// email (URL: /authentications/registrations/confirmations/show?confirmation_token=…).
// The controller has already validated the token and emits the necessary
// props; we render a single "Aktifkan Akun" button that POSTs to the
// existing Rails action which signs the user in + redirects.
//
// This form uses a native <form method="POST"> (not XHR) because the Rails
// response is a redirect with flash — perfect for plain HTML form behavior.

const drawCircle = keyframes`
  0%   { stroke-dashoffset: 170; }
  100% { stroke-dashoffset: 0; }
`;

const Mark = styled.svg`
  width: 72px;
  height: 72px;
  display: block;
  margin-bottom: 8px;

  .circle {
    fill: none;
    stroke: ${brand_success};
    stroke-width: 3;
    stroke-dasharray: 170;
    stroke-dashoffset: 170;
    animation: ${drawCircle} 680ms ease-out forwards;
  }

  .envelope {
    fill: ${brand_orange_deep};
  }
`;

const Heading = styled.h1`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 10px;
  color: #111827;

  [data-theme="dark"] & { color: ${dark.text_primary}; }
`;

const Body = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.7;

  & strong {
    color: ${brand_orange_deep};
    font-weight: 700;
  }

  [data-theme="dark"] & { color: ${dark.text_secondary}; }
`;

const SecondaryLinks = styled.div`
  margin-top: 20px;
  font-size: 13px;
  color: #6b7280;

  & a {
    color: ${brand_orange_deep};
    font-weight: 600;
    text-decoration: none;
  }

  & a:hover, & a:focus-visible {
    text-decoration: underline;
  }

  [data-theme="dark"] & { color: ${dark.text_secondary}; }
`;

export default function ConfirmEmail() {
  const {
    user_id: userId,
    email,
    confirmation_token: confirmationToken,
    redirect_after_login: redirectAfterLogin,
    confirm_action_path: actionPath,
  } = usePage().props;

  return (
    <>
      <Head>
        <title>Konfirmasi Email — Taxsam Learning Center</title>
      </Head>
      <GlobalStyle />
      <AuthLayout imageSide="right">
        <Mark viewBox="0 0 64 64" aria-hidden="true">
          <circle className="circle" cx="32" cy="32" r="28" />
          <rect className="envelope" x="18" y="24" width="28" height="18" rx="2" />
          <path d="M18 26 L32 36 L46 26" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
        </Mark>
        <Heading>Selangkah lagi!</Heading>
        <Body>
          Akun dengan email <strong>{email}</strong> siap diaktifkan. Klik tombol di bawah untuk menyelesaikan pendaftaran dan langsung masuk ke dashboard.
        </Body>
        <form method="POST" action={actionPath}>
          <input type="hidden" name="authenticity_token" value={getCsrfToken() || ""} />
          <input type="hidden" name="user[user_id]" value={userId || ""} />
          <input type="hidden" name="user[confirmation_token]" value={confirmationToken || ""} />
          {redirectAfterLogin && (
            <input type="hidden" name="redirect_after_login" value={redirectAfterLogin} />
          )}
          <SubmitButton>Aktifkan Akun</SubmitButton>
        </form>
        <SecondaryLinks>
          Bukan Anda? <Link href="/">Kembali ke beranda</Link>
        </SecondaryLinks>
      </AuthLayout>
    </>
  );
}
